import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { type ChartSeries } from '@org/ui';
import { useAnalytics } from '../analytics-context';
import { ReportCard } from '../report-card';
import { ReportTable } from '../report-table';
import { TimeSeriesCard } from '../time-series-card';
import { ChatTranscriptModal } from '../chat-transcript-modal';
import { formatDateTime } from '../format';

export function ChatReports() {
  const { t, i18n } = useTranslation('admin');
  const { input, compareInput, search, update } = useAnalytics();
  const overTime = trpc.analytics.chat.overTime.useQuery(input);
  const cmpOverTime = trpc.analytics.chat.overTime.useQuery(compareInput ?? input, { enabled: Boolean(compareInput) });
  const byStudent = trpc.analytics.chat.byStudent.useQuery(input);
  const sessions = trpc.analytics.chat.sessions.useQuery(input);

  const lang = i18n.language;
  const series: ChartSeries[] = [
    { key: 'messages', label: t('analytics.chat.overTime.series.messages') },
    { key: 'sessions', label: t('analytics.chat.overTime.series.sessions') },
    { key: 'students', label: t('analytics.chat.overTime.series.students') },
  ];

  return (
    <div className="space-y-6">
      <TimeSeriesCard
        title={t('analytics.chat.overTime.title')}
        description={t('analytics.chat.overTime.description')}
        data={overTime.data}
        cmpData={cmpOverTime.data}
        series={series}
        isLoading={overTime.isLoading}
        error={overTime.error}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <ReportCard
          title={t('analytics.chat.byStudent.title')}
          description={t('analytics.chat.byStudent.description')}
          isLoading={byStudent.isLoading}
          error={byStudent.error}
          isEmpty={byStudent.data?.length === 0}
        >
          <ReportTable
            rows={byStudent.data ?? []}
            rowKey={(r) => r.userId}
            defaultSort="messages"
            pageSize={15}
            searchKeys={['name', 'email']}
            columns={[
              { key: 'name', label: t('analytics.common.student'), value: (r) => r.name },
              { key: 'sessions', label: t('analytics.chat.byStudent.sessions'), value: (r) => r.sessions, align: 'right' },
              { key: 'messages', label: t('analytics.chat.byStudent.messages'), value: (r) => r.messages, align: 'right' },
              {
                key: 'last',
                label: t('analytics.chat.byStudent.lastMessage'),
                value: (r) => r.lastMessageAt,
                render: (r) => formatDateTime(r.lastMessageAt, lang),
              },
            ]}
          />
        </ReportCard>

        <ReportCard
          title={t('analytics.chat.sessions.title')}
          description={t('analytics.chat.sessions.description')}
          isLoading={sessions.isLoading}
          error={sessions.error}
          isEmpty={sessions.data?.length === 0}
        >
          <ReportTable
            rows={sessions.data ?? []}
            rowKey={(r) => r.sessionId}
            defaultSort="created"
            pageSize={15}
            searchKeys={['name', 'email', 'title']}
            onRowClick={(r) => update({ session: r.sessionId })}
            columns={[
              { key: 'name', label: t('analytics.common.student'), value: (r) => r.name },
              { key: 'title', label: t('analytics.chat.sessions.titleColumn'), value: (r) => r.title, className: 'text-muted-foreground' },
              {
                key: 'created',
                label: t('analytics.chat.sessions.createdAt'),
                value: (r) => r.createdAt,
                render: (r) => formatDateTime(r.createdAt, lang),
              },
              { key: 'messages', label: t('analytics.chat.byStudent.messages'), value: (r) => r.messages, align: 'right' },
            ]}
          />
          <p className="mt-2 text-xs text-muted-foreground">{t('analytics.chat.sessions.hint')}</p>
        </ReportCard>
      </div>

      {search.session ? (
        <ChatTranscriptModal sessionId={search.session} onClose={() => update({ session: undefined })} />
      ) : null}
    </div>
  );
}
